/**
 * Mailbox writes: the mocks behind `POST /domains/{domain}/owner-mailbox` (create) and
 * `POST /domains/{domain}/accounts/{email}/password` (rotate).
 *
 * TEMPORARY, like the seed beside it. Both answer after the simulated round trip in
 * {@link MAILBOX_SIMULATED}. The create succeeds unless a test asks it to refuse (the knobs below), so
 * the two refusals the UI must handle are reachable without a backend:
 *  - `conflict`: the address already exists at the provider. A create NEVER answers that with a new
 *    password (it would lock the owner out of the mail client they already set up); rotating is the
 *    way forward.
 *  - `weak-password`: the provider's own strength rules refused the password, with its own words.
 *  - `insufficient-funds`: the owner's main wallet could not pay for a paid create (`402`). Nothing
 *    was created; the same create succeeds after a top-up.
 */
import { generateMailboxPassword } from '../pages/mailbox/password';
import { MAILBOX_SIMULATED, type MailboxError } from './mailbox-records';


/** How a demo create is told to answer. `ok` is the real path. */
export type MockCreateOutcome = 'ok' | 'conflict' | 'weak-password' | 'insufficient-funds';

/** The provider's refusal of a password, verbatim as the service passes it through. One of the
 *  examples the service documents; the real ones vary with the rule that was broken. */
export const MOCK_WEAK_PASSWORD_MESSAGE = 'password not strong enough';

/**
 * The simulation's knob for tests: make the next creates refuse, one outcome per create, in order.
 * The component-test hosts set it, as they do `simulateNextSettlementFailure` for a payment. Gone
 * with the mock.
 */
let refuseNextCreates: Exclude<MockCreateOutcome, 'ok'>[] = [];
export const simulateNextCreateRefusal = (...outcomes: Exclude<MockCreateOutcome, 'ok'>[]): void => {
  refuseNextCreates = [...outcomes];
};

/**
 * The simulation's knob for the create's latency: the next create answers after `ms` instead of
 * {@link MAILBOX_SIMULATED}'s figure (once, then back to it), so a test can make a create slower than
 * the provisioning screen's run, or `'never'` answers at all. Gone with the mock.
 */
let nextCreateDelayMs: number | 'never' | null = null;
export const simulateNextCreateDelay = (ms: number | 'never'): void => {
  nextCreateDelayMs = ms;
};

/** A create, as the endpoint would answer: resolves once the mailbox exists with its credentials,
 *  returned once (the operator's password when they typed one, else one "the service" generated), or
 *  rejects with the service's {@link MailboxError}. */
export const createMockMailbox = (
  email: string,
  requested: MockCreateOutcome = 'ok',
  password?: string,
): Promise<{ email: string; password: string }> =>
  new Promise((resolve, reject) => {
    const queued = refuseNextCreates.shift() ?? 'ok';
    const outcome = requested === 'ok' ? queued : requested;
    const delay = nextCreateDelayMs ?? MAILBOX_SIMULATED.createMs;
    nextCreateDelayMs = null;
    if (delay === 'never') return;
    setTimeout(() => {
      if (outcome === 'conflict') {
        const error: MailboxError = {
          code: 'conflict',
          message: `mailbox ${email} already exists; rotate its password instead`,
        };
        reject(error);
      } else if (outcome === 'weak-password') {
        const error: MailboxError = { code: 'invalid_request', message: MOCK_WEAK_PASSWORD_MESSAGE };
        reject(error);
      } else if (outcome === 'insufficient-funds') {
        // The service's wording, which still says credits: the section shows its own words instead.
        const error: MailboxError = { code: 'insufficient_credits', message: 'insufficient credits' };
        reject(error);
      } else resolve({ email, password: password || generateMailboxPassword() });
    }, delay);
  });

/**
 * The simulation's knob for a refused rotation: the next rotation rejects with the provider's `400`
 * carrying `message` (once), as a typed password its strength rules dislike would. Gone with the mock.
 */
let refuseNextRotation: string | null = null;
export const simulateNextRotateRefusal = (message: string): void => {
  refuseNextRotation = message;
};

/** A rotation, as the endpoint would answer: the password it was sent (else one it generated),
 *  returned once, or the provider's refusal when a test asked for one. */
export const rotateMockMailboxPassword = (
  email: string,
  password?: string,
): Promise<{ email: string; password: string }> =>
  new Promise((resolve, reject) => {
    const refusal = refuseNextRotation;
    refuseNextRotation = null;
    setTimeout(() => {
      if (refusal !== null) {
        const error: MailboxError = { code: 'invalid_request', message: refusal };
        reject(error);
      } else resolve({ email, password: password || generateMailboxPassword() });
    }, MAILBOX_SIMULATED.resetMs);
  });
